// Aba administrativa para gerenciar índices econômicos
// Exibe status, permite upload manual de JSON e força atualizações
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useIndices } from '@/hooks/useIndices';
import { ECONOMIC_SERIES } from '@/services/indicesService';
import { formatPct } from '@/utils/formatters';
import { useQueryClient } from '@tanstack/react-query';
import { 
  RefreshCw, 
  Upload, 
  Calendar, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2,
  Database,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SeriesStatus {
  code: number;
  name: string;
  lastValue: number | null;
  lastDate: string | null;
  isUpdated: boolean;
  type: 'monthly' | 'daily';
}

export const IndicesTab = () => {
  const [seriesStatus, setSeriesStatus] = useState<SeriesStatus[]>([]);
  const [jsonInput, setJsonInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const { 
    getAllStatus, 
    forceUpdate, 
    loading, 
    formatDateBR 
  } = useIndices();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Carrega status das séries
  const loadSeriesStatus = async () => {
    console.log('📊 [DEBUG] Carregando status das séries...');
    
    try {
      const statusData = await getAllStatus();
      console.log('📊 [DEBUG] Dados retornados getAllStatus:', statusData);
      
      const series: SeriesStatus[] = Object.entries(ECONOMIC_SERIES).map(([code, info]) => {
        const numCode = parseInt(code);
        const data = statusData[numCode];
        
        const seriesInfo = {
          code: numCode,
          name: info.name,
          lastValue: data?.value || null,
          lastDate: data?.date || null,
          isUpdated: !!data,
          type: info.type
        };
        
        console.log(`📊 [DEBUG] Série ${numCode} (${info.name}):`, {
          hasData: !!data,
          value: data?.value,
          date: data?.date
        });
        
        return seriesInfo;
      });
      
      console.log('📊 [DEBUG] Status processado:', series.length, 'séries');
      setSeriesStatus(series);
    } catch (error) {
      console.error('❌ [DEBUG] Erro ao carregar status:', error);
    }
  };

  useEffect(() => {
    loadSeriesStatus();
  }, []);

  // Atualização manual de séries mensais
  const handleUpdateMonthly = async () => {
    console.log('🔄 [DEBUG] Iniciando atualização mensal...');
    const monthlySeries = [433, 188, 226, 4390]; // IPCA, INPC, TR, SELIC mensal
    
    console.log('🔄 [DEBUG] Séries mensais a atualizar:', monthlySeries);
    
    try {
      const success = await forceUpdate(monthlySeries, 'monthly');
      console.log('✅ [DEBUG] Resultado atualização mensal:', success);
      
      if (success) {
        console.log('🔄 [DEBUG] Invalidando queries React Query...');
        await queryClient.invalidateQueries({ queryKey: ['indices-bcb'] });
        await queryClient.refetchQueries({ queryKey: ['indices-bcb'], type: 'active' });
        console.log('🔄 [DEBUG] Recarregando status das séries...');
        loadSeriesStatus();
      } else {
        console.error('❌ [DEBUG] Falha na atualização mensal');
      }
    } catch (error) {
      console.error('❌ [DEBUG] Erro durante atualização mensal:', error);
    }
  };

  // Atualização manual de séries diárias
  const handleUpdateDaily = async () => {
    console.log('🔄 [DEBUG] Iniciando atualização diária...');
    const dailySeries = [11]; // SELIC diária
    
    console.log('🔄 [DEBUG] Séries diárias a atualizar:', dailySeries);
    
    try {
      const success = await forceUpdate(dailySeries, 'daily');
      console.log('✅ [DEBUG] Resultado atualização diária:', success);
      
      if (success) {
        console.log('🔄 [DEBUG] Invalidando queries React Query...');
        await queryClient.invalidateQueries({ queryKey: ['indices-bcb'] });
        await queryClient.refetchQueries({ queryKey: ['indices-bcb'], type: 'active' });
        console.log('🔄 [DEBUG] Recarregando status das séries...');
        loadSeriesStatus();
      } else {
        console.error('❌ [DEBUG] Falha na atualização diária');
      }
    } catch (error) {
      console.error('❌ [DEBUG] Erro durante atualização diária:', error);
    }
  };

  // Upload manual de arquivo JSON - FUNCIONAL
  const handleJsonUpload = async () => {
    if (!jsonInput.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Cole o conteúdo JSON antes de importar"
      });
      return;
    }

    try {
      setUploading(true);
      
      // Valida JSON
      const jsonData = JSON.parse(jsonInput);
      console.log('📤 [DEBUG] JSON para importar:', jsonData);
      
      // Processa e insere dados diretamente
      const insertPromises = Object.entries(jsonData).map(async ([seriesCode, dataArray]) => {
        if (!Array.isArray(dataArray) || dataArray.length === 0) return null;
        
        const latestEntry = dataArray[0]; // Pega o primeiro (mais recente)
        const numCode = parseInt(seriesCode);
        
        // Converte data brasileira para formato ISO
        const [day, month, year] = latestEntry.data.split('/');
        const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        
        // Converte valor brasileiro para número
        const numValue = parseFloat(latestEntry.valor.replace(',', '.'));
        
        console.log(`📥 [DEBUG] Inserindo série ${numCode}: ${numValue} em ${isoDate}`);
        
        // Usa upsert para inserir/atualizar
        const { error } = await supabase
          .from('economic_indices')
          .upsert({
            series_code: numCode,
            date: isoDate,
            value: numValue,
            source: 'Manual Import'
          }, {
            onConflict: 'series_code,date'
          });
          
        if (error) throw error;
        return { series: numCode, success: true };
      });
      
      const results = await Promise.all(insertPromises);
      const successCount = results.filter(r => r?.success).length;
      
      console.log(`✅ [DEBUG] Importação concluída: ${successCount} séries inseridas`);
      
      toast({
        title: "Importação realizada com sucesso",
        description: `${successCount} séries foram atualizadas`
      });
      
      // Recarrega status
      await loadSeriesStatus();
      setJsonInput('');
      
    } catch (error) {
      console.error('❌ [DEBUG] Erro na importação:', error);
      toast({
        variant: "destructive",
        title: "Erro na importação",
        description: error instanceof Error ? error.message : "Formato JSON inválido"
      });
    } finally {
      setUploading(false);
    }
  };

  // Determina cor do badge baseado no status
  const getStatusVariant = (series: SeriesStatus) => {
    if (!series.isUpdated) return 'destructive';
    
    if (!series.lastDate) return 'secondary';
    
    const lastUpdate = new Date(series.lastDate);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (series.type === 'daily' && daysDiff > 3) return 'destructive';
    if (series.type === 'monthly' && daysDiff > 45) return 'destructive';
    
    return 'default';
  };

  // Função auxiliar para obter texto do status
  const getStatusText = (series: SeriesStatus): string => {
    if (!series.lastValue) return "Sem dados";
    if (!series.lastDate) return "Sem data";
    
    const lastUpdate = new Date(series.lastDate);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Verifica se está desatualizado baseado no tipo
    const isStale = (series.type === 'daily' && diffDays > 3) || (series.type === 'monthly' && diffDays > 45);
    
    if (isStale) {
      return `Desatualizado (${diffDays} dias)`;
    }
    
    if (diffDays === 0) return "Atualizado hoje";
    if (diffDays === 1) return "Atualizado ontem";
    return `Atualizado há ${diffDays} dias`;
  };

  const getStatusIcon = (series: SeriesStatus) => {
    const variant = getStatusVariant(series);
    if (variant === 'destructive') return <AlertCircle className="h-4 w-4" />;
    if (variant === 'secondary') return <Clock className="h-4 w-4" />;
    return <CheckCircle2 className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Controles de Atualização */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Atualizações Manuais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={handleUpdateMonthly}
              disabled={loading}
              className="w-full"
              size="sm"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Atualizando...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Atualizar Mensais
                </>
              )}
            </Button>
            <Button 
              onClick={handleUpdateDaily}
              disabled={loading}
              className="w-full"
              size="sm"
              variant="outline"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Atualizando...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Atualizar SELIC Diária
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Status Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Séries Ativas:</span>
                <Badge variant="outline">{seriesStatus.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Atualizadas:</span>
                <Badge variant="default">
                  {seriesStatus.filter(s => s.isUpdated).length}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>Pendentes:</span>
                <Badge variant="destructive">
                  {seriesStatus.filter(s => !s.isUpdated).length}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Importação Manual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setJsonInput(JSON.stringify({
                "433": [{"data": "31/01/2025", "valor": "0,11"}],
                "188": [{"data": "31/01/2025", "valor": "0,09"}],
                "226": [{"data": "31/01/2025", "valor": "0,00"}],
                "4390": [{"data": "31/01/2025", "valor": "0,95"}],
                "11": [{"data": "01/02/2025", "valor": "0,045"}]
              }, null, 2))}
              variant="outline"
              size="sm"
              className="w-full mb-2"
            >
              Carregar Exemplo
            </Button>
            <p className="text-xs text-muted-foreground">
              Clique para ver formato esperado
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Grid de Status das Séries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Status das Séries Econômicas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seriesStatus.map((series) => (
              <div
                key={series.code}
                className="p-4 border rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{series.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {series.code}
                    </Badge>
                  </div>
                  {getStatusIcon(series)}
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Último Valor:</span>
                    <span className="font-medium">
                      {series.lastValue ? formatPct(series.lastValue) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Data:</span>
                    <span className="font-medium">
                      {series.lastDate ? formatDateBR(series.lastDate) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tipo:</span>
                    <Badge 
                      variant={series.type === 'daily' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {series.type === 'daily' ? 'Diária' : 'Mensal'}
                    </Badge>
                  </div>
                </div>
                
                <Badge 
                  variant={getStatusVariant(series)}
                  className="w-full justify-center text-xs flex items-center gap-1"
                >
                  {getStatusIcon(series)}
                  {getStatusText(series)}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Manual de JSON */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Importação Manual via JSON
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="json-input">
              Conteúdo JSON (formato BCB/SGS)
            </Label>
            <Textarea
              id="json-input"
              placeholder='{"433": [{"data": "31/01/2025", "valor": "0,11"}], ...}'
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              rows={8}
              className="font-mono text-sm"
            />
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleJsonUpload}
              disabled={uploading || !jsonInput.trim()}
              className="flex-1"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar JSON
                </>
              )}
            </Button>
            
            <Button
              onClick={() => setJsonInput('')}
              variant="outline"
              disabled={uploading}
            >
              Limpar
            </Button>
            
            <Button
              onClick={loadSeriesStatus}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
            <p className="font-medium mb-1">Formato esperado:</p>
            <code>
              {`{
  "433": [{"data": "31/01/2025", "valor": "0,11"}],
  "188": [{"data": "31/01/2025", "valor": "0,09"}],
  "226": [{"data": "31/01/2025", "valor": "0,00"}],
  "4390": [{"data": "31/01/2025", "valor": "0,95"}],
  "11": [{"data": "01/02/2025", "valor": "0,045"}]
}`}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};